/**
 * Kanalregel in der Seitenleiste, neben der Zeitleiste: erst Kanäle, dann Apartments, dann der
 * Zeitraum. Der Server prüft jede vollständige Fassung von selbst; vormerken lässt sich erst, was
 * er in genau dieser Fassung geprüft hat.
 */
import { useState } from 'react';
import {
  Button,
  ConfirmDialog,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/shared/components';
import { ChannelCloseoutEditorFields } from './ChannelCloseoutEditorFields';
import { ChannelCloseoutLoadNotice } from './ChannelCloseoutLoadNotice';
import { useChannelCloseoutEditor } from './useChannelCloseoutEditor';

export function ChannelCloseoutEditorSheet({
  propertyGroupId,
  closeoutId,
  today,
  onClose,
  onOpenApartment,
}: {
  propertyGroupId: string;
  closeoutId: string | null;
  today: string;
  onClose: () => void;
  onOpenApartment: (propertyId: string, startDate: string, endDate: string) => void;
}) {
  const editor = useChannelCloseoutEditor({ propertyGroupId, closeoutId, onSaved: onClose });
  const { options, detail } = editor;
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  // Eine angefangene Regel geht weder durch Escape noch durch einen Klick daneben verloren. Während
  // des Vormerkens bleibt die Leiste offen: Scheitert es, stehen die Eingaben noch da.
  const requestClose = (then?: () => void) => {
    if (editor.save.isPending) return;
    const leave = () => {
      then?.();
      onClose();
    };
    if (editor.dirty) setPendingLeave(() => leave);
    else leave();
  };
  return (
    <>
      <Sheet open onOpenChange={(open) => !open && requestClose()}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="text-ink font-serif text-[22px] font-bold leading-tight">
              {closeoutId ? 'Kanalregel bearbeiten' : 'Kanalregel anlegen'}
            </SheetTitle>
            <SheetDescription className="text-ink-soft mt-1 text-sm">
              Schließt nur die gewählten Kanäle. Preise und Mindestnächte bleiben, wie sie sind.
            </SheetDescription>
          </SheetHeader>
          {options.isError ? (
            <ChannelCloseoutLoadNotice
              message={
                editor.optionsProblem?.detail ??
                'Die für diese Regel nötigen Kanalzuordnungen konnten nicht geladen werden.'
              }
              onRetry={() => void options.refetch()}
            />
          ) : detail.isError ? (
            <ChannelCloseoutLoadNotice
              message="Die Regel konnte nicht geladen werden."
              onRetry={() => void detail.refetch()}
            />
          ) : (
            <form
              className="flex min-h-0 flex-1 flex-col"
              onSubmit={(event) => {
                event.preventDefault();
                if (editor.canSave) editor.save.mutate();
              }}
            >
              <SheetBody className="space-y-6">
                <ChannelCloseoutEditorFields
                  editor={editor}
                  today={today}
                  closeoutId={closeoutId}
                  onOpenApartment={(propertyId, startDate, endDate) =>
                    requestClose(() => onOpenApartment(propertyId, startDate, endDate))
                  }
                />
              </SheetBody>
              <SheetFooter>
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-pill h-11"
                  disabled={editor.save.isPending}
                  onClick={() => requestClose()}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="brand"
                  className="rounded-pill h-11"
                  disabled={!editor.canSave}
                  loading={editor.save.isPending}
                >
                  {closeoutId ? 'Änderung vormerken' : 'Regel vormerken'}
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>
      <ConfirmDialog
        open={pendingLeave !== null}
        onOpenChange={(open) => !open && setPendingLeave(null)}
        title="Eingaben verwerfen?"
        description="Die Kanalregel ist noch nicht vorgemerkt. Beim Schließen gehen die Eingaben verloren."
        confirmLabel="Verwerfen"
        cancelLabel="Weiter bearbeiten"
        onConfirm={() => {
          const leave = pendingLeave;
          setPendingLeave(null);
          leave?.();
        }}
      />
    </>
  );
}
